export type UpgradeRequiredListener = (version?: string) => void;

const listeners = new Set<UpgradeRequiredListener>();

/**
 * Subscribes to mandatory-update responses observed by shared API clients.
 *
 * @param listener - Callback invoked for each HTTP 426 response.
 * @returns A function that removes the callback.
 * @throws Never.
 * @sideEffects Adds one process-local listener until the returned function runs.
 */
export function subscribeToUpgradeRequired(listener: UpgradeRequiredListener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/**
 * Publishes a mandatory-update response to the mounted Office host.
 *
 * @param response - The 426 response containing the deployed build identity.
 * @returns A promise that settles after listeners have been notified.
 * @throws Never; one failing listener cannot prevent other hosts from receiving the signal.
 * @sideEffects Clones and reads the response body, then invokes every current listener.
 */
export async function notifyUpgradeRequired(response: Response): Promise<void> {
  let version: string | undefined;
  try {
    const payload = (await response.clone().json()) as { currentVersion?: unknown };
    if (typeof payload.currentVersion === "string" && payload.currentVersion.trim()) {
      version = payload.currentVersion.trim();
    }
  } catch {
    // The controller retains its defensive fallback when a non-conforming 426 has no JSON body.
  }

  for (const listener of listeners) {
    try {
      listener(version);
    } catch {
      // Update signaling must not replace the original HTTP response path.
    }
  }
}
